// JSON-LD профиля /tutors/<slug> — в <head>, на сервере (head маршрута).
//
// Репетитор — Person, кружок — EducationalOrganization (с адресом — ещё и LocalBusiness). Предметы — knowsAbout
// на языке страницы, цена — makesOffer (Offer + UnitPriceSpecification «за
// урок» / «в месяц»), где занимается — areaServed: район, районы выезда;
// онлайн — без района.
//
// У Person в schema.org нет свойства areaServed, поэтому для репетитора оно
// лежит в Offer (makesOffer.areaServed) — это его законное место. У кружка
// areaServed и address — на самой организации.
//
// ЧЕГО ЗДЕСЬ НЕТ НАМЕРЕННО: телефона, WhatsApp, email (их нет и в
// map_partners_public), точного адреса и координат (точка на карте видна
// только у building, но в разметку не идёт: только район).
// Фото — адрес серверного прокси /api/map/photo/<id> (бакет приватный).
//
// Проверка — scripts/verify-tutor-seo.mts.
import { MAP_COPY, TUTORS_PATH } from "./map-copy";
import { partnerMeta } from "./partner-meta";
import { priceText, unitShown } from "@/lib/price-format";
import type { SiteLang } from "@/components/site/SiteChrome";
import { BAKU_CITY, DISTRICTS, labelOf, subjectItemsFor } from "@/data/map-taxonomy";
import { DISTRICT_LABELS } from "@/lib/map-places";
import type { PublicPartner } from "@/lib/map-public.functions";

const SITE = "https://yolutap.com";

const CITY: Record<SiteLang, string> = { az: "Bakı", ru: "Баку", en: "Baku" };

const SERVICE: Record<SiteLang, Record<PublicPartner["kind"], string>> = {
  az: { tutor: "Repetitor dərsləri", club: "Dərnək məşğələləri" },
  ru: { tutor: "Занятия с репетитором", club: "Занятия в кружке" },
  en: { tutor: "Tutoring lessons", club: "Club classes" },
};

export const profileUrl = (lang: SiteLang, slug: string) => `${SITE}${TUTORS_PATH[lang]}/${slug}`;

const area = (code: string, lang: SiteLang) => ({
  "@type": "AdministrativeArea",
  name: labelOf(DISTRICT_LABELS, code, lang),
  containedInPlace: { "@type": "City", name: CITY[lang] },
});

/** Районы, где идут занятия: свой район (building) и районы выезда. Онлайн — пусто. */
function areasOf(p: PublicPartner, lang: SiteLang) {
  if (p.address_mode === "online") return [];
  // Вне Баку — город целиком, без районов.
  if (p.city && p.city !== BAKU_CITY) return [{ "@type": "City", name: p.city_name?.[lang] ?? p.city }];
  const codes = [...(p.address_mode === "building" && p.district ? [p.district] : []), ...p.travel_districts];
  return [...new Set(codes)].map((c) => area(c, lang));
}

type Json = Record<string, unknown>;

export function partnerJsonLd(p: PublicPartner, lang: SiteLang): Json {
  const items = subjectItemsFor(p.kind);
  const subjects = p.subjects.map((s) => labelOf(items, s, lang).replace(/\s*\([^)]*\)/g, ""));
  const url = profileUrl(lang, p.slug);
  const areas = areasOf(p, lang);
  const areaServed = areas.length === 1 ? areas[0] : areas.length ? areas : undefined;
  // Единица — только если её подпись честная (формат подтверждён, или кружок с месячной ценой):
  // «dərs», «ay · ayda 12 dərs», «10 dərs». Нет — offer без unitText.
  const unit = unitShown(p) ? (priceText(p, lang).main ?? "").replace(/^[^/]*\/\s*/, "") || null : null;
  const offer: Json = {
    "@type": "Offer",
    itemOffered: {
      "@type": "Service",
      name: SERVICE[lang][p.kind],
      ...(subjects.length ? { serviceType: subjects.join(", ") } : {}),
      ...(p.address_mode === "online" ? { availableChannel: { "@type": "ServiceChannel", name: "Online" } } : {}),
    },
    ...(areaServed ? { areaServed } : {}),
    ...(p.price_azn !== null
      ? {
          price: p.price_azn,
          priceCurrency: "AZN",
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: p.price_azn,
            priceCurrency: "AZN",
            ...(unit ? { unitText: unit } : {}),
            ...(unit && p.price_unit === "month" ? { unitCode: "MON" } : {}),
          },
        }
      : {}),
  };
  const common: Json = {
    name: p.name,
    url,
    description: partnerMeta(p, lang).description,
    ...(p.photo_url ? { image: SITE + p.photo_url } : {}),
    ...(subjects.length ? { knowsAbout: subjects } : {}),
    makesOffer: offer,
  };
  if (p.kind === "tutor") return { "@context": "https://schema.org", "@type": "Person", ...common, jobTitle: lang === "az" ? "Repetitor" : lang === "ru" ? "Репетитор" : "Tutor" };
  const otherCity = p.address_mode === "building" && p.city && p.city !== BAKU_CITY ? (p.city_name?.[lang] ?? p.city) : "";
  const district = p.address_mode === "building" && p.district && !otherCity ? labelOf(DISTRICT_LABELS, p.district, lang) : "";
  // Кружок с адресом (район или город) — ещё и LocalBusiness (28.10.2026); без адреса — только организация.
  return {
    "@context": "https://schema.org",
    "@type": district || otherCity ? ["EducationalOrganization", "LocalBusiness"] : "EducationalOrganization",
    ...common,
    ...(areaServed ? { areaServed } : {}),
    ...(district
      ? { address: { "@type": "PostalAddress", addressLocality: district, addressRegion: CITY[lang], addressCountry: "AZ" } }
      : otherCity
        ? { address: { "@type": "PostalAddress", addressLocality: otherCity, addressCountry: "AZ" } }
        : {}),
  };
}

/** Текст для <script type="application/ld+json">: «<» → \u003c, «</script>» внутри строки не закроет тег. */
export const jsonLdText = (o: unknown) =>
  JSON.stringify(o).replace(/</g, "\\u003c").replace(/>/g, "\\u003e").replace(/&/g, "\\u0026").replace(/\u2028/g, "\\u2028").replace(/\u2029/g, "\\u2029");
